import { z } from 'zod';
import { IdSchema, MoneySchema, TimestampSchema } from './common.js';
import { DateOnlySchema } from './invoices.js';

/**
 * Subscriptions and the monthly billing run (docs/28 B3).
 *
 * A direct client is billed monthly, in arrears, for what it had in the period:
 * seats at the most in use on any day of it, numbers and a call plan by the day, a
 * seat's setup price once, and a postpaid client's calls. Its first and last month
 * are prorated. The run on its billing day drafts the invoice.
 */

export const SUBSCRIPTION_KINDS = ['seat', 'number', 'plan'] as const;
export const CHARGE_KINDS = ['seat', 'number', 'plan', 'usage'] as const;

/** One line of what a period is billed. */
export const ChargeLineSchema = z.object({
  kind: z.enum(CHARGE_KINDS),
  description: z.string(),
  quantity: z.string(),
  unit_price: z.string(),
  amount: MoneySchema,
});
export type ChargeLine = z.infer<typeof ChargeLineSchema>;

/** The current period so far, as its run would draft it if nothing changes before then. */
export const MonthlyChargesSchema = z.object({
  /** ArcNet bills its direct clients; a reseller's clients are billed by the reseller (B4). */
  billed_by: z.enum(['platform', 'reseller']),
  currency: z.string().length(3),
  period_start: DateOnlySchema,
  /** The last day of the period. */
  period_end: DateOnlySchema,
  /** The day the period is invoiced: the next billing day. */
  billing_date: DateOnlySchema,
  lines: z.array(ChargeLineSchema),
  /** Excluding VAT. */
  subtotal: MoneySchema,
  /** Seats with no extension type, which have no price and are not billed. */
  untyped_seats: z.number().int(),
});
export type MonthlyCharges = z.infer<typeof MonthlyChargesSchema>;

export const BILLING_RUN_STATUSES = ['drafted', 'nothing_to_bill', 'failed'] as const;

export const BillingRunSchema = z.object({
  id: IdSchema,
  tenant_id: IdSchema.nullable(),
  account_name: z.string(),
  currency: z.string().length(3),
  period_start: DateOnlySchema,
  /** The last day of the period. */
  period_end: DateOnlySchema,
  status: z.enum(BILLING_RUN_STATUSES),
  invoice: z.object({
    id: IdSchema, number: z.string().nullable(), status: z.string(), total: MoneySchema,
  }).nullable(),
  warnings: z.array(z.string()),
  error: z.string().nullable(),
  /** Run by a person rather than the job. */
  by_hand: z.boolean(),
  updated_at: TimestampSchema,
});
export type BillingRun = z.infer<typeof BillingRunSchema>;

export const BillingRunsSchema = z.object({
  data: z.array(BillingRunSchema),
  /** Direct clients with seats that have no extension type, which no run can bill. */
  untyped_seats: z.array(z.object({
    tenant_id: IdSchema, account_name: z.string(), seats: z.number().int(),
  })),
});
export type BillingRuns = z.infer<typeof BillingRunsSchema>;

/** Run what is due now: every direct client, or one. */
export const BillingRunRequestSchema = z.object({
  tenant_id: IdSchema.optional(),
  /** Replace the draft the period already has. Never an issued invoice. */
  rerun: z.boolean().default(false),
}).strict().refine((b) => !b.rerun || !!b.tenant_id, 'Choose the account to run again.');
export type BillingRunRequest = z.input<typeof BillingRunRequestSchema>;

export const BillingRunReportSchema = z.object({
  drafted: z.number().int(),
  nothing_to_bill: z.number().int(),
  already_run: z.number().int(),
  failed: z.number().int(),
  /** Why, for the first few that failed. */
  errors: z.array(z.string()),
});
export type BillingRunReport = z.infer<typeof BillingRunReportSchema>;

/**
 * Whether an invoice 21 days overdue suspends this client. Platform staff's
 * decision, never the client's.
 */
export const DunningSettingsSchema = z.object({ auto_suspend: z.boolean() }).strict();
export type DunningSettings = z.infer<typeof DunningSettingsSchema>;
